import type { AuthPort, AuthCredentials, AuthSession } from '../../../ports/AuthPort';
import { gaiaHttpUrl } from '../url/GaiaUrls';

type GaiaAuthResponse = {
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
  session?: {
    access_token?: string;
    refresh_token?: string;
    expires_in?: number;
  };
};

function toAuthSession(data: GaiaAuthResponse, fallbackRefreshToken?: string): AuthSession {
  const tokenSource = data.session ?? data;
  const refreshToken = tokenSource.refresh_token ?? fallbackRefreshToken;
  if (!tokenSource.access_token) {
    throw new Error('Auth response missing session tokens');
  }

  return {
    accessToken: tokenSource.access_token,
    refreshToken,
    expiresAt: Date.now() + (tokenSource.expires_in ?? 3600) * 1000,
  };
}

export class GaiaAuthHttp implements AuthPort {
  constructor(private baseUrl: string) {}

  async login(credentials: AuthCredentials): Promise<AuthSession> {
    if (credentials.kind === 'device') {
      const deviceRes = await fetch(gaiaHttpUrl(this.baseUrl, '/api/v0.3/auth/device'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          device_id: credentials.deviceId,
          platform: credentials.platform ?? 'unknown',
          device_name: credentials.deviceName ?? 'Aeonia device',
        }),
      });
      if (!deviceRes.ok) throw new Error(`device sign-in failed: ${deviceRes.status}`);
      return toAuthSession(await deviceRes.json());
    }

    const res = await fetch(gaiaHttpUrl(this.baseUrl, '/api/v0.3/auth/login'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: credentials.email, password: credentials.password }),
    });
    if (!res.ok) throw new Error(`Auth login failed: ${res.status}`);
    return toAuthSession(await res.json());
  }

  async refresh(refreshToken: string, accessToken?: string): Promise<AuthSession> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
    const res = await fetch(gaiaHttpUrl(this.baseUrl, '/api/v0.3/auth/refresh'), {
      method: 'POST',
      headers,
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!res.ok) throw new Error(`Auth refresh failed: ${res.status}`);
    return toAuthSession(await res.json(), refreshToken);
  }

  async validate(accessToken: string): Promise<boolean> {
    const res = await fetch(gaiaHttpUrl(this.baseUrl, '/api/v0.3/auth/validate'), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token: accessToken }),
    });
    if (!res.ok) return false;

    const data = (await res.json()) as { valid?: unknown };
    return data.valid === true;
  }

  async logout(accessToken: string): Promise<void> {
    await fetch(gaiaHttpUrl(this.baseUrl, '/api/v0.3/auth/logout'), {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  }
}